import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { Loader2Icon, PlayIcon, RefreshCwIcon, ZapIcon } from "lucide-react";
import { useState } from "react";

type WebhookActionParam = {
  name: string;
  type: "string[]" | "boolean";
};

type WebhookActionDef = {
  key: string;
  endpoint: string;
  method: "get" | "post";
  category: "sync" | "refresh";
  params?: WebhookActionParam[];
};

const WEBHOOK_ACTIONS: WebhookActionDef[] = [
  // Refresh
  {
    key: "refresh-companies-linked",
    endpoint: "/webhook/action/refresh-companies-linked",
    method: "get",
    category: "refresh",
  },
  {
    key: "refresh-team-knowledge-files",
    endpoint: "/webhook/action/refresh-team-knowledge-files",
    method: "post",
    category: "refresh",
  },
  {
    key: "refresh-team-knowledge-websites",
    endpoint: "/webhook/action/refresh-team-knowledge-websites",
    method: "post",
    category: "refresh",
  },
  // Sync
  {
    key: "sync-stripe-datas",
    endpoint: "/webhook/action/sync-stripe-datas",
    method: "post",
    category: "sync",
    params: [
      { name: "teamIds", type: "string[]" },
      { name: "reconcile", type: "boolean" },
    ],
  },
  {
    key: "sync-clerk-users",
    endpoint: "/webhook/action/sync-clerk-users",
    method: "post",
    category: "sync",
    params: [{ name: "userIds", type: "string[]" }],
  },
  {
    key: "sync-hubspot-integrations",
    endpoint: "/webhook/action/sync-hubspot-integrations",
    method: "post",
    category: "sync",
    params: [{ name: "teamIds", type: "string[]" }],
  },
  {
    key: "sync-intercom",
    endpoint: "/webhook/action/sync-intercom",
    method: "post",
    category: "sync",
    params: [
      { name: "teamIds", type: "string[]" },
      { name: "userIds", type: "string[]" },
      { name: "dryRun", type: "boolean" },
    ],
  },
  {
    key: "sync-posthog",
    endpoint: "/webhook/action/sync-posthog",
    method: "post",
    category: "sync",
    params: [
      { name: "teamIds", type: "string[]" },
      { name: "userIds", type: "string[]" },
    ],
  },
];

function parseStringArray(value: string): string[] {
  if (!value.trim()) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function GodBoardWebhookActions() {
  const api = useApi();
  const { t } = useTranslation();
  const { toast } = useToast();
  const toastError = useToastError();

  const [triggeringAction, setTriggeringAction] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<WebhookActionDef | null>(
    null
  );
  const [formValues, setFormValues] = useState<Record<string, string | boolean>>(
    {}
  );

  const triggerAction = async (action: WebhookActionDef, body?: Record<string, unknown>) => {
    setTriggeringAction(action.key);
    try {
      if (action.method === "get") {
        await api.get(action.endpoint);
      } else {
        await api.post(action.endpoint, body ?? {});
      }
      toast({
        variant: "default",
        title: t("admin.godBoard.webhookActions.toast.success", {
          action: t(`admin.godBoard.webhookActions.actions.${action.key}.label`),
        }),
      });
    } catch {
      toastError();
    } finally {
      setTriggeringAction(null);
    }
  };

  const handleParamsSubmit = async () => {
    if (!activeAction?.params) return;

    const body: Record<string, unknown> = {};
    for (const param of activeAction.params) {
      const value = formValues[param.name];
      if (param.type === "string[]") {
        const arr = parseStringArray((value as string) ?? "");
        if (arr.length > 0) body[param.name] = arr;
      } else if (param.type === "boolean") {
        body[param.name] = value === true;
      }
    }

    setActiveAction(null);
    setFormValues({});
    await triggerAction(activeAction, body);
  };

  const openParamsDialog = (action: WebhookActionDef) => {
    setFormValues({});
    setActiveAction(action);
  };

  const refreshActions = WEBHOOK_ACTIONS.filter((a) => a.category === "refresh");
  const syncActions = WEBHOOK_ACTIONS.filter((a) => a.category === "sync");

  const renderActionRow = (action: WebhookActionDef) => {
    const label = t(`admin.godBoard.webhookActions.actions.${action.key}.label`);
    const description = t(
      `admin.godBoard.webhookActions.actions.${action.key}.description`
    );
    const isTriggering = triggeringAction === action.key;
    const hasParams = action.params && action.params.length > 0;

    return (
      <div key={action.key} className="flex items-center gap-4">
        {hasParams ? (
          <Button
            variant="outline"
            size="sm"
            disabled={isTriggering}
            onClick={() => openParamsDialog(action)}
            className="min-w-[180px] justify-start"
          >
            {isTriggering ? (
              <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ZapIcon className="mr-2 h-4 w-4" />
            )}
            {label}
          </Button>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isTriggering}
                className="min-w-[180px] justify-start"
              >
                {isTriggering ? (
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <PlayIcon className="mr-2 h-4 w-4" />
                )}
                {label}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("admin.godBoard.webhookActions.confirm.title", {
                    action: label,
                  })}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t("admin.godBoard.webhookActions.confirm.description")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  {t("admin.godBoard.webhookActions.confirm.cancel")}
                </AlertDialogCancel>
                <AlertDialogAction onClick={() => triggerAction(action)}>
                  {t("admin.godBoard.webhookActions.confirm.trigger")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={activeAction !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActiveAction(null);
            setFormValues({});
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {activeAction &&
                t("admin.godBoard.webhookActions.paramsDialog.title", {
                  action: t(
                    `admin.godBoard.webhookActions.actions.${activeAction.key}.label`
                  ),
                })}
            </DialogTitle>
            <DialogDescription>
              {t("admin.godBoard.webhookActions.paramsDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {activeAction?.params?.map((param) => (
              <div key={param.name} className="space-y-2">
                {param.type === "boolean" ? (
                  <div className="flex items-center gap-3">
                    <Switch
                      id={param.name}
                      checked={(formValues[param.name] as boolean) ?? false}
                      onCheckedChange={(checked) =>
                        setFormValues((prev) => ({
                          ...prev,
                          [param.name]: checked,
                        }))
                      }
                    />
                    <Label htmlFor={param.name}>
                      {t(
                        `admin.godBoard.webhookActions.params.${param.name}`
                      )}
                    </Label>
                  </div>
                ) : (
                  <>
                    <Label htmlFor={param.name}>
                      {t(
                        `admin.godBoard.webhookActions.params.${param.name}`
                      )}
                    </Label>
                    <Input
                      id={param.name}
                      placeholder="id1, id2, id3"
                      value={(formValues[param.name] as string) ?? ""}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          [param.name]: e.target.value,
                        }))
                      }
                    />
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActiveAction(null);
                setFormValues({});
              }}
            >
              {t("admin.godBoard.webhookActions.paramsDialog.cancel")}
            </Button>
            <Button onClick={handleParamsSubmit}>
              {t("admin.godBoard.webhookActions.paramsDialog.trigger")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-3">
        <h3 className="text-lg font-semibold">
          {t("admin.godBoard.webhookActions.title")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("admin.godBoard.webhookActions.description")}
        </p>

        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <RefreshCwIcon className="h-4 w-4" />
              {t("admin.godBoard.webhookActions.categories.refresh")}
            </h4>
            <div className="space-y-2 pl-6">
              {refreshActions.map(renderActionRow)}
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <ZapIcon className="h-4 w-4" />
              {t("admin.godBoard.webhookActions.categories.sync")}
            </h4>
            <div className="space-y-2 pl-6">
              {syncActions.map(renderActionRow)}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
